import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useId } from "react";

import { CategoryView } from "~/components/reports/menu-performance/views/category-view";
import { ItemView } from "~/components/reports/menu-performance/views/item-view";
import { MenuView } from "~/components/reports/menu-performance/views/menu-view";
import { enumIncludes, parseEnumValue } from "~/lib/parse-enum-value";
import {
  useSearchParamsDateRange,
  requireDateRange,
} from "~/utils/search-params-date-range";

const MENU_PERF_GROUP_BY = ["item", "category", "menu"] as const;
const isMenuPerfGroupBy = enumIncludes(MENU_PERF_GROUP_BY);

export const Route = createFileRoute(
  "/_authenticated/reports/menu-performance"
)({
  component: MenuPerformanceReport,
});

function MenuPerformanceReport() {
  const searchParams = useSearch({ strict: false });
  const locationId = searchParams.location;
  const groupBy = parseEnumValue(
    searchParams.groupBy,
    isMenuPerfGroupBy,
    "item"
  );
  const [dateRange] = useSearchParamsDateRange();
  const tableId = useId();

  if (!locationId) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <h3 className="subtitle text-foreground mb-2">Location Required</h3>
          <p className="body text-muted-foreground">
            Please select a location from the filters above to view Menu
            Performance.
          </p>
        </div>
      </div>
    );
  }

  if (!(dateRange.from && dateRange.to)) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <h3 className="subtitle text-foreground mb-2">Date Range Required</h3>
          <p className="body text-muted-foreground">
            Please select a date range from the filters above.
          </p>
        </div>
      </div>
    );
  }

  const typedDateRange = requireDateRange(dateRange);
  if (!typedDateRange) {
    return null;
  }

  const getTitle = () => {
    switch (groupBy) {
      case "item":
        return "Menu Item Performance";
      case "category":
        return "Menu Category Performance";
      case "menu":
        return "Menu Performance";
      default:
        return "Menu Performance";
    }
  };

  const getDescription = () => {
    switch (groupBy) {
      case "item":
        return "Sales performance by individual menu items";
      case "category":
        return "Sales performance by menu categories";
      case "menu":
        return "Sales performance by complete menus";
      default:
        return "Sales performance data";
    }
  };

  const renderView = () => {
    switch (groupBy) {
      case "item":
        return (
          <ItemView
            dateRange={typedDateRange}
            locationId={locationId}
            tableId={tableId}
          />
        );
      case "category":
        return (
          <CategoryView
            dateRange={typedDateRange}
            locationId={locationId}
            tableId={tableId}
          />
        );
      case "menu":
        return (
          <MenuView
            dateRange={typedDateRange}
            locationId={locationId}
            tableId={tableId}
          />
        );
      default:
        return (
          <ItemView
            dateRange={typedDateRange}
            locationId={locationId}
            tableId={tableId}
          />
        );
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="subtitle text-foreground mb-2">{getTitle()}</h2>
        <p className="body text-foreground">{getDescription()}</p>
      </div>

      {renderView()}
    </div>
  );
}
